import { useTranslation } from 'module/hooks/use-translation';
import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import { faPlus, faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from '@rocketlane/rocket-router';
import { PageTemplateLite } from 'api/page-template/types';
import {
  useGetCompanyPageTemplatesWithScrollPagination,
  useUpdateCompanyPageTemplateOrderMutation,
  useDeleteCompanyPageTemplateMutation,
} from 'queries/company-page-template';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import { CompanyTemplateTable } from 'components/organisms/company-page-template/list-table/table';
import {
  CompanyPageTemplatesNoSearchMatchState,
  CompanyPageTemplatesNoTemplatesState,
} from './empty-state';
import { ContentWrapper, ListLoader } from '../../templates/styles';
import type { CompanyPageTemplatePermissions } from 'types/nitro-pages';

const COMPANY_TEMPLATES_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

interface CompanyPageTemplatesProps {
  companyPageTemplatePermissions?: CompanyPageTemplatePermissions;
}

export const CompanyPageTemplates = ({
  companyPageTemplatePermissions,
}: CompanyPageTemplatesProps = {}) => {
  const canWrite = companyPageTemplatePermissions?.canWrite !== false;
  const canDelete = companyPageTemplatePermissions?.canDelete !== false;
  const canCreate = canWrite;
  const pageTitle = useTranslation('company_page_templates.title');
  const navigate = useNavigate();
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    templates: apiTemplates,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetCompanyPageTemplatesWithScrollPagination({
    limit: COMPANY_TEMPLATES_PAGE_SIZE,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  });
  const [templates, setTemplates] = useState<PageTemplateLite[]>([]);
  const deleteCompanyPageTemplateMutation =
    useDeleteCompanyPageTemplateMutation();
  const updateCompanyPageTemplateOrderMutation =
    useUpdateCompanyPageTemplateOrderMutation();

  useEffect(() => {
    setTemplates(apiTemplates);
  }, [apiTemplates]);

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDelete = async (id: string) => {
    try {
      await deleteCompanyPageTemplateMutation.mutateAsync(Number(id));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleReorder = async (
    newTemplates: PageTemplateLite[],
    movedTemplateId: number,
    newIndex: number,
  ) => {
    try {
      setTemplates(newTemplates);
      await updateCompanyPageTemplateOrderMutation.mutateAsync({
        templateId: movedTemplateId,
        order: newIndex,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const hasActiveSearch = Boolean(debouncedSearch);
  const showEmptyNoTemplates = !hasActiveSearch && total === 0;
  const showEmptyNoSearchMatch = hasActiveSearch && total === 0;

  return (
    <Argon
      documentTitle={pageTitle}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faFileLines} size={20} />}
          title={pageTitle}
        />
      }
      description={<FormattedMessage id="company_page_templates.description" />}
      actions={{
        secondary: (
          <FlexWrapperWithShortGap>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
            />

            {canCreate && (
              <Button
                icon={<FontAwesomeIcon icon={faPlus} size={12} />}
                type="primary"
                onClick={() => navigate(ROUTE_PATHS.NEW)}
              >
                <FormattedMessage id="new_template" />
              </Button>
            )}
          </FlexWrapperWithShortGap>
        ),
      }}
      content={
        isLoading ? (
          <ListLoader />
        ) : (
          <ContentWrapper>
            {showEmptyNoTemplates ? (
              <CompanyPageTemplatesNoTemplatesState
                onPrimaryAction={
                  canCreate ? () => navigate(ROUTE_PATHS.NEW) : undefined
                }
              />
            ) : showEmptyNoSearchMatch ? (
              <CompanyPageTemplatesNoSearchMatchState
                onPrimaryAction={() =>
                  searchInputRef.current?.closeSearchInput()
                }
              />
            ) : (
              <CompanyTemplateTable
                templates={templates}
                onEdit={
                  canWrite ? (id: string) => navigate(`${id}`) : undefined
                }
                onDelete={canDelete ? handleDelete : undefined}
                onReorder={canWrite ? handleReorder : undefined}
                loading={isLoading}
                loadingMore={isFetchingNextPage}
                hasMore={hasNextPage}
                onLoadMore={handleLoadMore}
              />
            )}
          </ContentWrapper>
        )
      }
    />
  );
};
